import { Clipboard, HStack, Icon, IconButton, type IconProps, Spacer, Text } from '@chakra-ui/react'
import type { FC, ReactNode } from 'react'
import type { IconType } from 'react-icons'
import { LuCheck, LuClipboard } from 'react-icons/lu'
import { PiProjectorScreen } from 'react-icons/pi'
import { Link, useNavigate } from 'react-router-dom'

import { useColorModeValue } from '@app/components/ui/color-mode'
import { Tooltip } from '@app/components/ui/tooltip'
import cardFontSize from '@app/shared/cards/cardFontSize'
import cardIconBoxSize from '@app/shared/cards/cardIconBoxSize'
import CardSection from '@app/shared/cards/components/cardSection'
import type { CardSize } from '@app/types'
import ICON_MAP from '@app/utils/iconMap'

interface DomainObjectTypeProps extends IconProps {
  title?: string
  icon?: IconType
  color?: string
  route?: string
  commentsCount?: number
  children?: ReactNode
}

export const DomainObjectType: FC<DomainObjectTypeProps> = ({
  icon,
  boxSize,
  color,
  title,
  fontSize,
  children,
  ...rest
}) => {
  const cardTypeColor = useColorModeValue(`${color}.600`, `${color}.300`)
  const IconComponent = icon

  return (
    <HStack>
      {icon && (
        <Icon boxSize={boxSize} color={cardTypeColor} {...icon} {...rest}>
          <IconComponent />
        </Icon>
      )}
      {title && (
        <Text color={cardTypeColor} fontSize={fontSize} fontWeight="semibold" whiteSpace="nowrap">
          {title}
        </Text>
      )}
      {children}
    </HStack>
  )
}

interface Props extends Omit<IconProps, 'size'> {
  title?: string
  icon?: IconType
  color?: string
  size?: CardSize
  route?: string
  commentsCount?: number
  children?: ReactNode
  nodeId?: string
  previewId?: string
}

const CardTypeSection: FC<Props> = ({
  title,
  icon,
  color = 'blue',
  size = 'normal',
  route,
  children,
  nodeId,
  previewId,
  ...rest
}) => {
  const navigate = useNavigate()
  const fontSize = cardFontSize('md', size)
  const boxSize = cardIconBoxSize(6, size)
  const sectionSpacing = size === 'normal' ? { px: 4, py: 2 } : { px: 6, py: 4 }

  return (
    <CardSection {...sectionSpacing} size={size} {...rest}>
      <HStack>
        <DomainObjectType icon={icon} boxSize={boxSize} color={color} title={title} fontSize={fontSize}>
          {children}
        </DomainObjectType>

        {route && (
          <>
            <Spacer />
            <HStack gap={1}>
              {nodeId && (
                <Clipboard.Root value={nodeId}>
                  <Tooltip content="Copy metric id" showArrow>
                    <Clipboard.Trigger asChild>
                      <IconButton variant="ghost" color="fg.subtle" onClick={(e) => e.stopPropagation()}>
                        <Clipboard.Indicator copied={<LuCheck />}>
                          <LuClipboard />
                        </Clipboard.Indicator>
                      </IconButton>
                    </Clipboard.Trigger>
                  </Tooltip>
                </Clipboard.Root>
              )}
              {previewId && (
                <Tooltip content="Show preview" showArrow>
                  <IconButton variant="ghost" color="fg.subtle" asChild>
                    <Link to={`preview/${previewId}`}>
                      <PiProjectorScreen />
                    </Link>
                  </IconButton>
                </Tooltip>
              )}
              <IconButton color="fg.subtle" aria-label="Details" onClick={() => navigate(route)} variant="ghost">
                <ICON_MAP.ExternalLink />
              </IconButton>
            </HStack>
          </>
        )}
      </HStack>
    </CardSection>
  )
}

export default CardTypeSection
